import React, { useEffect, useMemo, useState } from 'react';
import sharedStyles from './AdminShared.module.scss';
import styles from './AdminImages.module.scss';
import { useUserState } from '../../../providers/user-state.provider.ts';
import {
    archiveAdminImage,
    completeAdminImageUpload,
    createAdminImageUpload,
    fetchAdminImage,
    fetchAdminImagesPaged,
    patchAdminImage,
    restoreAdminImage,
    uploadToPresignedUrl
} from '../../../services/admin.service';
import {
    ImageDetailDto,
    ImageListItemDto,
    ImagePatchDto,
    ImageUploadInitRequestDto
} from '../../../types/admin.types';
import {
    formatBytes,
    getVariantLabel,
    getVariantOrder,
    getVariantUrl,
    IMAGE_STATUS_LABELS,
    mapImageDetail,
    mapImageListItem,
    normalizeVariantType
} from '../../../utils/image.utils';

type UploadStatus = 'queued' | 'uploading' | 'processing' | 'done' | 'failed';

interface UploadTask {
    id: string;
    file: File;
    status: UploadStatus;
    error?: string;
    imageId?: string;
}

const MAX_UPLOAD_BYTES = 25 * 1024 * 1024;

const emptyDetail: ImagePatchDto = {
    title: '',
    altText: '',
    caption: '',
    description: '',
    attribution: '',
    sourceUrl: ''
};

const deriveTitle = (fileName: string) => {
    const withoutExt = fileName.replace(/\.[^/.]+$/, '');
    return withoutExt.replace(/[_-]+/g, ' ').trim();
};

const formatDateTime = (value?: string | null) => {
    if (!value) {
        return 'N/A';
    }
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) {
        return value;
    }
    return parsed.toLocaleString();
};

const AdminImages = () => {
    const { getUserInfo } = useUserState();
    const token = getUserInfo()?.token;

    const [listData, setListData] = useState<ImageListItemDto[]>([]);
    const [pageIndex, setPageIndex] = useState(1);
    const [pageSize, setPageSize] = useState(24);
    const [totalPages, setTotalPages] = useState(1);
    const [searchInput, setSearchInput] = useState('');
    const [appliedSearch, setAppliedSearch] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');
    const [includeArchived, setIncludeArchived] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);

    const [selectedId, setSelectedId] = useState<string | null>(null);
    const [selectedDetail, setSelectedDetail] = useState<ImageDetailDto | null>(null);
    const [detailForm, setDetailForm] = useState<ImagePatchDto>(emptyDetail);
    const [detailVariantType, setDetailVariantType] = useState<number | null>(null);
    const [isSaving, setIsSaving] = useState(false);

    const [uploadQueue, setUploadQueue] = useState<UploadTask[]>([]);
    const [isUploading, setIsUploading] = useState(false);
    const [uploadFileInput, setUploadFileInput] = useState<FileList | null>(null);
    const [uploadTitle, setUploadTitle] = useState('');
    const [uploadAltText, setUploadAltText] = useState('');
    const [uploadCaption, setUploadCaption] = useState('');
    const [uploadDescription, setUploadDescription] = useState('');
    const [uploadAttribution, setUploadAttribution] = useState('');
    const [uploadSourceUrl, setUploadSourceUrl] = useState('');

    const loadImages = async (page: number, query: string, includeArchivedValue: boolean) => {
        setIsLoading(true);
        setErrorMessage(null);
        try {
            const response = await fetchAdminImagesPaged(page, pageSize, query, includeArchivedValue, token);
            const results = response?.Results ?? [];
            setListData(results.map(mapImageListItem));
            setTotalPages(response?.TotalPages ?? 1);
        } catch (error) {
            setErrorMessage('Unable to load images.');
        } finally {
            setIsLoading(false);
        }
    };

    const loadDetail = async (id: string) => {
        try {
            const response = await fetchAdminImage(id, token);
            const mapped = mapImageDetail(response);
            setSelectedDetail(mapped);
            setDetailForm({
                title: mapped.title ?? '',
                altText: mapped.altText ?? '',
                caption: mapped.caption ?? '',
                description: mapped.description ?? '',
                attribution: mapped.attribution ?? '',
                sourceUrl: mapped.sourceUrl ?? ''
            });

            const preferred = mapped.variants.find((variant) => normalizeVariantType(variant.variantType) === 1)
                ?? mapped.variants.find((variant) => normalizeVariantType(variant.variantType) === 0)
                ?? mapped.variants[0];
            setDetailVariantType(normalizeVariantType(preferred?.variantType));
        } catch (error) {
            setErrorMessage('Unable to load image details.');
        }
    };

    useEffect(() => {
        loadImages(pageIndex, appliedSearch, includeArchived);
    }, [pageIndex, appliedSearch, includeArchived, pageSize]);

    useEffect(() => {
        if (!selectedId) {
            setSelectedDetail(null);
            setDetailForm(emptyDetail);
            return;
        }

        void loadDetail(selectedId);
    }, [selectedId]);

    const filteredResults = useMemo(() => {
        if (statusFilter === 'all') {
            return listData;
        }
        const target = Number(statusFilter);
        if (Number.isNaN(target)) {
            return listData;
        }
        return listData.filter((item) => Number(item.status) === target);
    }, [listData, statusFilter]);

    const orderedVariants = useMemo(() => {
        if (!selectedDetail) {
            return [];
        }
        return [...(selectedDetail.variants ?? [])]
            .filter((variant) => Boolean(variant.url))
            .sort((a, b) => getVariantOrder(a.variantType) - getVariantOrder(b.variantType));
    }, [selectedDetail]);

    const activePreviewUrl = useMemo(() => {
        if (!selectedDetail) {
            return null;
        }
        if (detailVariantType != null) {
            const variant = orderedVariants.find((item) => normalizeVariantType(item.variantType) === detailVariantType);
            if (variant?.url) {
                return variant.url;
            }
        }
        return getVariantUrl(selectedDetail.variants, 1) ?? getVariantUrl(selectedDetail.variants, 0);
    }, [detailVariantType, orderedVariants, selectedDetail]);

    const updateTask = (id: string, updates: Partial<UploadTask>) => {
        setUploadQueue((prev) => prev.map((task) => (task.id === id ? { ...task, ...updates } : task)));
    };

    const processUploads = async (tasks: UploadTask[]) => {
        if (isUploading) {
            return;
        }
        setIsUploading(true);
        let lastImageId: string | null = null;

        for (const task of tasks) {
            if (task.file.size > MAX_UPLOAD_BYTES) {
                updateTask(task.id, { status: 'failed', error: 'File exceeds 25MB limit.' });
                continue;
            }

            updateTask(task.id, { status: 'uploading', error: undefined });
            try {
                const applyMetadata = tasks.length === 1;
                const payload: ImageUploadInitRequestDto = {
                    filename: task.file.name,
                    fileSize: task.file.size,
                    contentType: task.file.type || 'image/jpeg',
                    title: applyMetadata ? (uploadTitle.trim() || deriveTitle(task.file.name)) : deriveTitle(task.file.name),
                    altText: applyMetadata ? (uploadAltText.trim() || null) : null,
                    caption: applyMetadata ? (uploadCaption.trim() || null) : null,
                    description: applyMetadata ? (uploadDescription.trim() || null) : null,
                    attribution: applyMetadata ? (uploadAttribution.trim() || null) : null,
                    sourceUrl: applyMetadata ? (uploadSourceUrl.trim() || null) : null
                };

                const init = await createAdminImageUpload(payload, token);
                await uploadToPresignedUrl(init.presignedUrl, task.file, { 'x-amz-acl': 'public-read' });
                updateTask(task.id, { status: 'processing', imageId: init.imageId });
                await completeAdminImageUpload(init.imageId, token);
                updateTask(task.id, { status: 'done' });
                lastImageId = init.imageId;
            } catch (error: any) {
                updateTask(task.id, { status: 'failed', error: error?.message || 'Upload failed.' });
            }
        }

        setIsUploading(false);
        await loadImages(pageIndex, appliedSearch, includeArchived);
        if (lastImageId) {
            setSelectedId(lastImageId);
        }
    };

    const handleUploadSubmit = async (event: React.FormEvent) => {
        event.preventDefault();
        if (!uploadFileInput || uploadFileInput.length === 0) {
            setErrorMessage('Select at least one image file.');
            return;
        }

        const tasks: UploadTask[] = Array.from(uploadFileInput).map((file) => ({
            id: `${file.name}-${Date.now()}-${Math.random().toString(16).slice(2)}`,
            file,
            status: 'queued'
        }));

        setUploadQueue((prev) => [...tasks, ...prev]);
        setUploadFileInput(null);
        await processUploads(tasks);
    };

    const handleSaveMetadata = async () => {
        if (!selectedDetail) {
            return;
        }
        setIsSaving(true);
        setErrorMessage(null);
        try {
            const updated = await patchAdminImage(selectedDetail.id, detailForm, token);
            const mapped = mapImageDetail(updated);
            setSelectedDetail(mapped);
            await loadImages(pageIndex, appliedSearch, includeArchived);
        } catch (error: any) {
            setErrorMessage(error?.message || 'Unable to update image.');
        } finally {
            setIsSaving(false);
        }
    };

    const handleArchiveToggle = async () => {
        if (!selectedDetail) {
            return;
        }
        try {
            if (selectedDetail.isArchived) {
                await restoreAdminImage(selectedDetail.id, token);
            } else {
                await archiveAdminImage(selectedDetail.id, token);
            }
            await loadImages(pageIndex, appliedSearch, includeArchived);
            await loadDetail(selectedDetail.id);
        } catch (error) {
            setErrorMessage('Unable to update archive status.');
        }
    };

    return (
        <div className={sharedStyles.page}>
            <header className={sharedStyles.header}>
                <div>
                    <h1>Images</h1>
                    <p>Upload, manage, and reuse media across promos, collections, and highlights.</p>
                </div>
            </header>

            {errorMessage ? <div className={sharedStyles.muted}>{errorMessage}</div> : null}

            <div className={sharedStyles.content}>
                <section className={`${sharedStyles.panel} ${styles.listPanel}`}>
                    <div className={styles.listHeader}>
                        <h2>Library</h2>
                        <div className={styles.searchRow}>
                            <input
                                value={searchInput}
                                onChange={(event) => setSearchInput(event.target.value)}
                                placeholder="Search title, alt text, or filename"
                            />
                            <button
                                type="button"
                                className={sharedStyles.secondary}
                                onClick={() => {
                                    setAppliedSearch(searchInput.trim());
                                    setPageIndex(1);
                                }}
                            >
                                Search
                            </button>
                        </div>
                        <div className={styles.filters}>
                            <select value={statusFilter} onChange={(event) => setStatusFilter(event.target.value)}>
                                <option value="all">All statuses</option>
                                <option value="0">Pending</option>
                                <option value="1">Processing</option>
                                <option value="2">Ready</option>
                                <option value="3">Failed</option>
                            </select>
                            <select
                                value={pageSize}
                                onChange={(event) => {
                                    setPageSize(Number(event.target.value));
                                    setPageIndex(1);
                                }}
                            >
                                <option value={12}>12 / page</option>
                                <option value={24}>24 / page</option>
                                <option value={48}>48 / page</option>
                            </select>
                            <label className={styles.checkbox}>
                                <input
                                    type="checkbox"
                                    checked={includeArchived}
                                    onChange={(event) => {
                                        setIncludeArchived(event.target.checked);
                                        setPageIndex(1);
                                    }}
                                />
                                Include archived
                            </label>
                        </div>
                    </div>

                    {isLoading ? (
                        <div className={sharedStyles.muted}>Loading images...</div>
                    ) : filteredResults.length === 0 ? (
                        <div className={sharedStyles.empty}>No images found.</div>
                    ) : (
                        <div className={styles.grid}>
                            {filteredResults.map((item) => (
                                <button
                                    key={item.id}
                                    type="button"
                                    className={item.id === selectedId ? styles.cardSelected : styles.card}
                                    onClick={() => setSelectedId(item.id)}
                                >
                                    <div className={styles.thumbnail}>
                                        {item.thumbnailUrl ? (
                                            <img src={item.thumbnailUrl} alt={item.title ?? item.originalFileName} />
                                        ) : (
                                            <div className={styles.thumbnailPlaceholder}>No preview</div>
                                        )}
                                    </div>
                                    <div className={styles.cardMeta}>
                                        <strong>{item.title ?? item.originalFileName}</strong>
                                        <span>{item.width}x{item.height}px</span>
                                        <span>{IMAGE_STATUS_LABELS[Number(item.status)] ?? 'Unknown'}</span>
                                    </div>
                                </button>
                            ))}
                        </div>
                    )}

                    <div className={styles.pagination}>
                        <button
                            type="button"
                            className={sharedStyles.secondary}
                            disabled={pageIndex <= 1}
                            onClick={() => setPageIndex((value) => Math.max(1, value - 1))}
                        >
                            Prev
                        </button>
                        <span>Page {pageIndex} of {totalPages}</span>
                        <button
                            type="button"
                            className={sharedStyles.secondary}
                            disabled={pageIndex >= totalPages}
                            onClick={() => setPageIndex((value) => Math.min(totalPages, value + 1))}
                        >
                            Next
                        </button>
                    </div>
                </section>

                <section className={`${sharedStyles.panel} ${styles.detailPanel}`}>
                    <h2>Details</h2>
                    {!selectedDetail ? (
                        <div className={sharedStyles.muted}>Select an image to edit metadata and variants.</div>
                    ) : (
                        <>
                            <div className={styles.preview}>
                                {activePreviewUrl ? (
                                    <img src={activePreviewUrl} alt={selectedDetail.altText ?? selectedDetail.title ?? 'Selected image'} />
                                ) : (
                                    <div className={styles.thumbnailPlaceholder}>Preview not available</div>
                                )}
                            </div>
                            <div className={styles.detailSummary}>
                                <div>
                                    <strong>{selectedDetail.title ?? selectedDetail.originalFileName}</strong>
                                    <div className={sharedStyles.muted}>
                                        {selectedDetail.width}x{selectedDetail.height}px - {IMAGE_STATUS_LABELS[Number(selectedDetail.status)] ?? 'Unknown'}
                                    </div>
                                </div>
                                <button type="button" className={sharedStyles.secondary} onClick={handleArchiveToggle}>
                                    {selectedDetail.isArchived ? 'Restore' : 'Archive'}
                                </button>
                            </div>
                            <div className={styles.detailGrid}>
                                <div className={styles.metaRow}>
                                    <span>Created</span>
                                    <strong>{formatDateTime(selectedDetail.createdDate)}</strong>
                                </div>
                                <div className={styles.metaRow}>
                                    <span>Updated</span>
                                    <strong>{formatDateTime(selectedDetail.updatedDate)}</strong>
                                </div>
                            </div>

                            <div className={styles.fieldGroup}>
                                <label>Preview variant</label>
                                <select
                                    value={detailVariantType ?? ''}
                                    onChange={(event) => setDetailVariantType(Number(event.target.value))}
                                >
                                    {orderedVariants.map((variant) => {
                                        const normalized = normalizeVariantType(variant.variantType);
                                        return (
                                            <option key={variant.url ?? String(normalized)} value={normalized ?? ''}>
                                                {getVariantLabel(variant.variantType)} - {variant.width}x{variant.height}
                                            </option>
                                        );
                                    })}
                                </select>
                            </div>

                            <div className={styles.variantList}>
                                {orderedVariants.map((variant) => (
                                    <div key={variant.url ?? String(variant.variantType)} className={styles.variantRow}>
                                        <div>
                                            <strong>{getVariantLabel(variant.variantType)}</strong>
                                            <div className={sharedStyles.muted}>
                                                {variant.width}x{variant.height} - {formatBytes(variant.fileSizeBytes)}
                                            </div>
                                        </div>
                                        {variant.url ? (
                                            <a href={variant.url} target="_blank" rel="noreferrer">Open</a>
                                        ) : (
                                            <span className={sharedStyles.muted}>Missing</span>
                                        )}
                                    </div>
                                ))}
                            </div>

                            <div className={styles.fieldGroup}>
                                <label>Title</label>
                                <input
                                    value={detailForm.title ?? ''}
                                    onChange={(event) => setDetailForm({ ...detailForm, title: event.target.value })}
                                    maxLength={200}
                                />
                            </div>
                            <div className={styles.fieldGroup}>
                                <label>Alt text</label>
                                <input
                                    value={detailForm.altText ?? ''}
                                    onChange={(event) => setDetailForm({ ...detailForm, altText: event.target.value })}
                                    maxLength={200}
                                />
                            </div>
                            <div className={styles.fieldGroup}>
                                <label>Caption</label>
                                <input
                                    value={detailForm.caption ?? ''}
                                    onChange={(event) => setDetailForm({ ...detailForm, caption: event.target.value })}
                                    maxLength={200}
                                />
                            </div>
                            <div className={styles.fieldGroup}>
                                <label>Description</label>
                                <textarea
                                    value={detailForm.description ?? ''}
                                    onChange={(event) => setDetailForm({ ...detailForm, description: event.target.value })}
                                    maxLength={2000}
                                />
                            </div>
                            <div className={styles.fieldGroup}>
                                <label>Attribution</label>
                                <input
                                    value={detailForm.attribution ?? ''}
                                    onChange={(event) => setDetailForm({ ...detailForm, attribution: event.target.value })}
                                    maxLength={200}
                                />
                            </div>
                            <div className={styles.fieldGroup}>
                                <label>Source URL</label>
                                <input
                                    value={detailForm.sourceUrl ?? ''}
                                    onChange={(event) => setDetailForm({ ...detailForm, sourceUrl: event.target.value })}
                                    maxLength={2000}
                                />
                            </div>
                            <div className={sharedStyles.actions}>
                                <button type="button" className={sharedStyles.primary} onClick={handleSaveMetadata} disabled={isSaving}>
                                    {isSaving ? 'Saving...' : 'Save Metadata'}
                                </button>
                            </div>
                        </>
                    )}

                    <div className={styles.divider} />

                    <h3>Upload new images</h3>
                    <form onSubmit={handleUploadSubmit} className={styles.uploadForm}>
                        <div className={styles.uploadNote}>
                            Bulk uploads use the filename as the title. Metadata fields apply when uploading a single file.
                        </div>
                        <input
                            type="file"
                            accept="image/png,image/jpeg,image/webp,image/gif,image/svg+xml"
                            multiple
                            onChange={(event) => setUploadFileInput(event.target.files)}
                        />
                        <div className={styles.uploadFields}>
                            <input
                                placeholder="Title (single upload only)"
                                value={uploadTitle}
                                onChange={(event) => setUploadTitle(event.target.value)}
                                maxLength={200}
                            />
                            <input
                                placeholder="Alt text (single upload only)"
                                value={uploadAltText}
                                onChange={(event) => setUploadAltText(event.target.value)}
                                maxLength={200}
                            />
                            <input
                                placeholder="Caption (single upload only)"
                                value={uploadCaption}
                                onChange={(event) => setUploadCaption(event.target.value)}
                                maxLength={200}
                            />
                            <input
                                placeholder="Attribution (single upload only)"
                                value={uploadAttribution}
                                onChange={(event) => setUploadAttribution(event.target.value)}
                                maxLength={200}
                            />
                            <input
                                placeholder="Source URL (single upload only)"
                                value={uploadSourceUrl}
                                onChange={(event) => setUploadSourceUrl(event.target.value)}
                                maxLength={2000}
                            />
                            <textarea
                                placeholder="Description (single upload only)"
                                value={uploadDescription}
                                onChange={(event) => setUploadDescription(event.target.value)}
                                maxLength={2000}
                            />
                        </div>
                        <div className={sharedStyles.actions}>
                            <button type="submit" className={sharedStyles.primary} disabled={isUploading}>
                                {isUploading ? 'Uploading...' : 'Start Upload'}
                            </button>
                        </div>
                    </form>

                    {uploadQueue.length > 0 ? (
                        <div className={styles.uploadQueue}>
                            {uploadQueue.map((task) => (
                                <div key={task.id} className={styles.queueItem}>
                                    <div>
                                        <strong>{task.file.name}</strong>
                                        <div className={sharedStyles.muted}>
                                            {formatBytes(task.file.size)} - {task.status}
                                            {task.error ? ` - ${task.error}` : ''}
                                        </div>
                                    </div>
                                    <span className={styles.queueStatus}>{task.status}</span>
                                </div>
                            ))}
                        </div>
                    ) : null}
                </section>
            </div>
        </div>
    );
};

export default AdminImages;
